import { taskApi as api } from './api';
import axios, { AxiosRequestConfig } from 'axios';
import { TaskListElement, TaskPermissionsMap } from 'types/store/tasksList';
import { TaskDetailToUpdate } from 'types/store/taskDetail';
import { JsonApiResource, JsonApiResponse } from 'types/store/jsonApi';
import { TaskAssignee, TaskProject } from './taskTypes';

export interface FetchTasksListApiParams {
  after?: string;
  sortBy?: string;
  order?: string;
  program?: string[];
  project?: string[];
  user?: string[];
  status?: string[];
  type?: string[];
  disabled?: boolean;
  startDateFrom?: string;
  startDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  completedDateFrom?: string;
  completedDateTo?: string;
  estimateHoursFrom?: string;
  estimateHoursTo?: string;
  actualHoursFrom?: string;
  actualHoursTo?: string;
  search?: string;
  includeTotalCount?: boolean;
  excludeProjectStatuses?: string[];
}

export interface FetchTasksListResponse {
  tasks: TaskListElement[];
  assignees: Record<string, TaskAssignee>;
  projects: Record<string, TaskProject>;
  links: {
    next: string | null;
  };
  totalCount?: number;
  permissions?: TaskPermissionsMap;
}

const getFetchTasksListUrlSearchParams = (params: FetchTasksListApiParams) => {
  const urlSearchParams = new URLSearchParams();

  if (params.program) {
    params.program.forEach((id) => {
      urlSearchParams.append(`program[]`, id);
    });
  }

  if (params.user) {
    params.user.forEach((id) => {
      urlSearchParams.append(`user[]`, id);
    });
  }

  if (params.project) {
    params.project.forEach((id) => {
      urlSearchParams.append('project[]', id);
    });
  }

  if (params.status) {
    params.status.forEach((status) => {
      urlSearchParams.append('status[]', status);
    });
  }

  if (params.type) {
    params.type.forEach((type) => {
      urlSearchParams.append('type[]', type);
    });
  }

  if (params.excludeProjectStatuses) {
    params.excludeProjectStatuses.forEach((status) => {
      urlSearchParams.append('excludeProjectStatuses[]', status);
    });
  }

  if (params.disabled) {
    urlSearchParams.append('disabled', 'true');
  }

  if (params.startDateFrom) {
    urlSearchParams.append('startDateFrom', params.startDateFrom);
  }

  if (params.startDateTo) {
    urlSearchParams.append('startDateTo', params.startDateTo);
  }

  if (params.dueDateFrom) {
    urlSearchParams.append('dueDateFrom', params.dueDateFrom);
  }

  if (params.dueDateTo) {
    urlSearchParams.append('dueDateTo', params.dueDateTo);
  }

  if (params.completedDateFrom) {
    urlSearchParams.append('completedDateFrom', params.completedDateFrom);
  }

  if (params.completedDateTo) {
    urlSearchParams.append('completedDateTo', params.completedDateTo);
  }

  if (params.estimateHoursFrom) {
    urlSearchParams.append(
      'estimateHoursFrom',
      params.estimateHoursFrom.toString()
    );
  }

  if (params.estimateHoursTo) {
    urlSearchParams.append(
      'estimateHoursTo',
      params.estimateHoursTo.toString()
    );
  }

  if (params.actualHoursFrom) {
    urlSearchParams.append(
      'actualHoursFrom',
      params.actualHoursFrom.toString()
    );
  }

  if (params.actualHoursTo) {
    urlSearchParams.append('actualHoursTo', params.actualHoursTo.toString());
  }

  if (params.search) {
    urlSearchParams.append('search', params.search);
  }

  if (params.sortBy) {
    urlSearchParams.append('sortBy', params.sortBy);
  }

  if (params.order) {
    urlSearchParams.append('order', params.order);
  }

  if (params.after) {
    urlSearchParams.append('after', params.after);
  }

  urlSearchParams.append('limit', '30');

  return urlSearchParams.toString();
};

export const fetchTasksList = async (params: FetchTasksListApiParams) => {
  const searchParams = getFetchTasksListUrlSearchParams(params);
  const url = `/v2/tasks?${searchParams}`;
  const config = {
    headers: {
      'include-total-count': params.includeTotalCount ? 'true' : 'false',
    },
  };

  const response = await api.get<FetchTasksListResponse>(url, config);
  if (axios.isAxiosError(response)) {
    throw response;
  }
  return response.data;
};

export interface ReorderTasksParams {
  projectId: string;
  taskId: string;
  newOrder: number;
}

export const reorderTasks = async (params: ReorderTasksParams) => {
  const url = `/v2/projects/${params.projectId}/tasks/${params.taskId}`;
  const response = await api.patch(url, {
    task: { newOrder: params.newOrder },
  });
  if (axios.isAxiosError(response)) {
    throw response;
  }
  return response.data;
};

export const exportTasksList = async (params: FetchTasksListApiParams) => {
  const searchParams = getFetchTasksListUrlSearchParams(params);
  const url = `/v2/tasks/exports/csv?${searchParams}`;
  const response = await api.get(url, { responseType: 'blob' });
  if (axios.isAxiosError(response)) {
    throw response;
  }
  return response;
};

export interface BulkUpdateTasksResponse {
  operationId: string;
  attemptedCount: number;
  successfulCount: number;
  failedCount: number;
  successfulTaskIds: string[];
  failedTaskIds: Map<string, Error>;
}

export const bulkUpdateTasks = async (
  url: string,
  task: Partial<TaskDetailToUpdate>,
  config?: AxiosRequestConfig
) => {
  const response = await api.patch<JsonApiResponse<BulkUpdateTasksResponse>>(
    url,
    { ...task },
    config
  );
  if (axios.isAxiosError(response)) {
    throw response;
  }
  return response.data;
};
export interface BulkDeleteTasksResponse {
  attemptedCount: number;
  successfulCount: number;
  failedCount: number;
  operationId: string;
}

export const bulkDeleteTasks = async (
  url: string,
  config?: AxiosRequestConfig
) => {
  const response = await api.delete<JsonApiResponse<BulkDeleteTasksResponse>>(
    url,
    config
  );
  if (axios.isAxiosError(response)) {
    throw response;
  }
  return response.data;
};

interface UndoBulkOperationParams {
  projectId: string;
  requestBody: JsonApiResource<{
    eventType: 'delete' | 'update';
    operationId: string;
  }>;
}

export interface UndoBulkOperationResponse {
  attemptedCount: number;
  successfulCount: number;
  failedCount: number;
}

export const undoBulkOperation = async (params: UndoBulkOperationParams) => {
  const response = await api.post<JsonApiResponse<UndoBulkOperationResponse>>(
    `/v3/projects/${params.projectId}/tasks/events/`,
    params.requestBody
  );
  if (axios.isAxiosError(response)) {
    throw response;
  }
  return response.data;
};
